/**
 * The swarm mark — "The Lattice": queen node at the centre of a honeycomb
 * cell, worker nodes on its vertices, faint comb edges behind (brand
 * direction, 19 Aug 2026). Rendered in currentColor so it sits on both the
 * dark shell and light surfaces.
 */
export function SwarmMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="none" aria-hidden>
      <g transform="translate(16,16)">
        <g stroke="currentColor" strokeWidth="1">
          <g strokeOpacity="0.4">
            <line x1="0" y1="0" x2="-8.25" y2="-4.76" />
            <line x1="0" y1="0" x2="8.25" y2="-4.76" />
            <line x1="0" y1="0" x2="0" y2="9.52" />
          </g>
          <g strokeOpacity="0.22">
            <line x1="-8.25" y1="-4.76" x2="-8.25" y2="4.76" />
            <line x1="8.25" y1="-4.76" x2="8.25" y2="4.76" />
            <line x1="-8.25" y1="4.76" x2="0" y2="9.52" />
            <line x1="8.25" y1="4.76" x2="0" y2="9.52" />
            <line x1="-8.25" y1="-4.76" x2="0" y2="-9.52" />
            <line x1="8.25" y1="-4.76" x2="0" y2="-9.52" />
          </g>
        </g>
        <g fill="currentColor">
          <circle cx="0" cy="0" r="3" />
          <circle cx="-8.25" cy="-4.76" r="1.8" fillOpacity="0.75" />
          <circle cx="8.25" cy="-4.76" r="1.8" fillOpacity="0.75" />
          <circle cx="0" cy="9.52" r="1.8" fillOpacity="0.75" />
          <circle cx="0" cy="-9.52" r="1.3" fillOpacity="0.4" />
          <circle cx="-8.25" cy="4.76" r="1.3" fillOpacity="0.4" />
          <circle cx="8.25" cy="4.76" r="1.3" fillOpacity="0.4" />
        </g>
      </g>
    </svg>
  );
}

/** Primary product lockup — swarm mark + lowercase wordmark. The console
 * brands as swarm first; WarriorLab appears as the supporting credit. */
export function SwarmLockup({
  tone = 'light',
  size = 'md',
  className,
}: {
  tone?: 'light' | 'dark';
  size?: 'md' | 'lg';
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center ${size === 'lg' ? 'gap-2.5' : 'gap-2'} ${className ?? ''}`}>
      <SwarmMark className={`${size === 'lg' ? 'h-9 w-9' : 'h-7 w-7'} text-[#2dd4bf]`} />
      <span
        className={`font-bold tracking-tight ${size === 'lg' ? 'text-[26px]' : 'text-[19px]'} ${
          tone === 'dark' ? 'text-white' : 'text-heading'
        }`}
      >
        swarm
      </span>
    </span>
  );
}

/** "Supported by WarriorLab" credit for footers — the inverse of the old
 * hierarchy now the product leads. */
export function SupportedByWarriorLab({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  const mark = tone === 'dark' ? '/warriorlab-logomark-white.svg' : '/warriorlab-logomark-navy.svg';
  return (
    <span
      className={
        tone === 'dark'
          ? 'inline-flex items-center gap-1.5 text-shell-muted'
          : 'inline-flex items-center gap-1.5 text-fg-subtle'
      }
    >
      <span className="text-[11px]">Supported by</span>
      <img src={mark} alt="" aria-hidden className="h-3.5 w-auto opacity-80" />
      <span
        className={`text-[10px] font-bold uppercase ${tone === 'dark' ? 'text-shell-fg/80' : 'text-fg/80'}`}
        style={{ letterSpacing: '0.18em' }}
      >
        WarriorLab
      </span>
    </span>
  );
}

/** "Powered by SWARM" lockup for footers and the login page. */
export function PoweredBySwarm({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  return (
    <span
      className={
        tone === 'dark'
          ? 'inline-flex items-center gap-1.5 text-shell-muted'
          : 'inline-flex items-center gap-1.5 text-fg-subtle'
      }
    >
      <span className="text-[11px]">Powered by</span>
      <SwarmMark className="h-3.5 w-3.5 text-[#2dd4bf]" />
      <span className={`text-[12px] font-bold tracking-tight ${tone === 'dark' ? 'text-shell-fg' : 'text-fg'}`}>
        swarm
      </span>
    </span>
  );
}
